import { useState, useMemo } from 'react';
import useAppStore from '../../store/app.store';
import { IcXCircle, IcAlertTriangle, IcCheckCircle, IcZap, IcBarChart, IcHandshake, IcX, IcCalendar } from '../../components/ui/Icons';
import PageHeader from '../../components/ui/PageHeader';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const STATUS_META = {
  nao_atingiu: { label: 'Não atingiu', cor: 'vermelho', icon: <IcXCircle size={13} /> },
  parcial:     { label: 'Parcial',     cor: 'amarelo',  icon: <IcAlertTriangle size={13} /> },
  atingiu:     { label: 'Atingiu',     cor: 'verde',    icon: <IcCheckCircle size={13} /> },
  superou:     { label: 'Superou!',   cor: 'roxo',     icon: <IcZap size={13} /> },
};

function BarMeta({ meta, realizado }) {
  const pct = meta > 0 ? Math.min(Math.round((realizado / meta) * 100), 120) : 0;
  const cor = pct >= 100 ? 'bg-green-500' : pct >= 70 ? 'bg-blue-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-400';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-500">Realizado vs Meta</span>
        <span className="font-semibold">{pct}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${cor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

export default function Acompanhamento() {
  const metas = useAppStore(s => s.metas);
  const followups = useAppStore(s => s.followups);
  const clientes = useAppStore(s => s.clientes);
  const usuarios = useAppStore(s => s.usuarios);
  const addMeta = useAppStore(s => s.addMeta);
  const updateMeta = useAppStore(s => s.updateMeta);
  const deleteMeta = useAppStore(s => s.deleteMeta);
  const addFollowup = useAppStore(s => s.addFollowup);
  const deleteFollowup = useAppStore(s => s.deleteFollowup);
  const getClienteNome = useAppStore(s => s.getClienteNome);
  const getUsuarioNome = useAppStore(s => s.getUsuarioNome);

  const [aba, setAba] = useState('metas');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [modalMeta, setModalMeta] = useState(false);
  const [modalFollowup, setModalFollowup] = useState(false);
  const [editandoMeta, setEditandoMeta] = useState(null);

  const metasFiltradas = useMemo(() =>
    metas.filter(m => !filtroCliente || m.cliente_id === filtroCliente),
    [metas, filtroCliente]
  );

  const followupsFiltrados = useMemo(() =>
    followups.filter(f => !filtroCliente || f.cliente_id === filtroCliente)
      .sort((a, b) => new Date(b.data_sessao) - new Date(a.data_sessao)),
    [followups, filtroCliente]
  );

  const calcularStatus = (meta, realizado) => {
    if (!meta || !realizado) return null;
    const pct = realizado / meta;
    if (pct < 0.8) return 'nao_atingiu';
    if (pct < 1.0) return 'parcial';
    if (pct < 1.1) return 'atingiu';
    return 'superou';
  };

  return (
    <div>
      <PageHeader
        title="Acompanhamento Comercial"
        subtitle="Metas, follow-ups e desenvolvimento das equipes de vendas dos clientes"
        actions={
          <div className="flex gap-2">
            <button onClick={() => setModalFollowup(true)} className="btn-secondary">+ Follow-up</button>
            <button onClick={() => setModalMeta(true)} className="btn-primary">+ Meta</button>
          </div>
        }
      />

      {/* Abas */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #2A3652' }}>
        {[
          { k: 'metas', l: 'Metas', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg> },
          { k: 'followups', l: 'Follow-ups', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
        ].map(({ k, l, icon }) => (
          <button key={k} onClick={() => setAba(k)} style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '8px 18px', fontSize: 14,
            fontWeight: aba === k ? 600 : 400,
            color: aba === k ? '#6C4CF5' : '#94A3B8',
            background: 'none', border: 'none',
            borderBottom: aba === k ? '2px solid #6C4CF5' : '2px solid transparent',
            cursor: 'pointer', transition: 'all .15s', marginBottom: -1,
          }}>
            {icon}{l}
          </button>
        ))}
      </div>

      {/* Filtro cliente */}
      <div className="mb-5">
        <select className="form-input w-auto" value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          <option value="">Todos os clientes</option>
          {clientes.filter(c => c.status === 'ativo').map(c => <option key={c.id} value={c.id}>{c.razao_social}</option>)}
        </select>
      </div>

      {aba === 'metas' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {metasFiltradas.map(m => {
            const statusCalculado = calcularStatus(m.meta_valor || m.meta_quantidade, m.realizado_valor || m.realizado_quantidade);
            const st = STATUS_META[m.status || statusCalculado] || STATUS_META.parcial;
            return (
              <div key={m.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">{getClienteNome(m.cliente_id)}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{m.periodo_tipo} · {m.periodo}</p>
                  </div>
                  <Badge variant={st.cor}>{st.icon} {st.label}</Badge>
                </div>

                <div className="space-y-4">
                  {m.meta_valor && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Faturamento</span>
                        <span>
                          <strong>{Number(m.realizado_valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                          <span className="text-gray-400"> / {Number(m.meta_valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                        </span>
                      </div>
                      <BarMeta meta={m.meta_valor} realizado={m.realizado_valor} />
                    </div>
                  )}
                  {m.meta_quantidade && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Volume (leads/vendas)</span>
                        <span>
                          <strong>{m.realizado_quantidade || 0}</strong>
                          <span className="text-gray-400"> / {m.meta_quantidade}</span>
                        </span>
                      </div>
                      <BarMeta meta={m.meta_quantidade} realizado={m.realizado_quantidade} />
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-4">
                  <button onClick={() => setEditandoMeta(m)} className="text-xs text-brand-600 hover:underline font-medium">Atualizar realizado</button>
                  <button onClick={() => { if (window.confirm('Excluir esta meta?')) { deleteMeta(m.id); toast.success('Meta excluída.'); } }} className="text-xs text-red-500 hover:underline">Excluir</button>
                </div>
              </div>
            );
          })}
          {!metasFiltradas.length && (
            <div className="col-span-2 text-center py-16 text-gray-400"><div className="flex justify-center mb-3"><IcBarChart size={40} /></div><p>Nenhuma meta cadastrada.</p></div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {followupsFiltrados.map(f => (
            <div key={f.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">{getClienteNome(f.cliente_id)}</h3>
                  <p className="text-xs text-gray-500 flex items-center gap-1"><IcCalendar size={11} /> {format(new Date(f.data_sessao), 'dd/MM/yyyy')} · {getUsuarioNome(f.responsavel_id)}</p>
                </div>
                {f.proxima_sessao && (
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Próxima sessão</p>
                    <p className="text-sm font-medium text-brand-700">{format(new Date(f.proxima_sessao), 'dd/MM/yyyy')}</p>
                  </div>
                )}
              </div>

              {f.checklist?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Checklist</p>
                  <div className="space-y-1">
                    {f.checklist.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span>{item.executado ? <IcCheckCircle size={13} style={{ color: '#22c55e' }} /> : item.combinado ? <IcCheckCircle size={13} style={{ color: '#cbd5e1' }} /> : '—'}</span>
                        <span className={item.executado ? 'text-gray-600 line-through' : 'text-gray-800'}>{item.item}</span>
                        {item.combinado && !item.executado && <Badge variant="amarelo">Pendente</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {f.observacoes && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">Observações</p>
                  <p className="text-sm text-gray-700">{f.observacoes}</p>
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <button onClick={() => { if (window.confirm('Excluir este follow-up?')) { deleteFollowup(f.id); toast.success('Follow-up excluído.'); } }} className="text-xs text-red-500 hover:underline">Excluir</button>
              </div>
            </div>
          ))}
          {!followupsFiltrados.length && (
            <div className="text-center py-16 text-gray-400"><div className="flex justify-center mb-3"><IcHandshake size={40} /></div><p>Nenhum follow-up registrado.</p></div>
          )}
        </div>
      )}

      {/* Modal meta */}
      <Modal open={modalMeta} onClose={() => setModalMeta(false)} title="Nova Meta">
        <FormMeta clientes={clientes.filter(c => c.status === 'ativo')}
          onSalvar={d => { addMeta(d); toast.success('Meta cadastrada!'); setModalMeta(false); }}
          onFechar={() => setModalMeta(false)} />
      </Modal>

      {/* Modal editar realizado */}
      <Modal open={!!editandoMeta} onClose={() => setEditandoMeta(null)} title="Atualizar realizado">
        {editandoMeta && (
          <FormAtualizarMeta meta={editandoMeta}
            onSalvar={d => { updateMeta(editandoMeta.id, d); toast.success('Meta atualizada!'); setEditandoMeta(null); }}
            onFechar={() => setEditandoMeta(null)} />
        )}
      </Modal>

      {/* Modal follow-up */}
      <Modal open={modalFollowup} onClose={() => setModalFollowup(false)} title="Novo Follow-up" size="lg">
        <FormFollowup clientes={clientes.filter(c => c.status === 'ativo')} usuarios={usuarios.filter(u => u.ativo)}
          onSalvar={d => { addFollowup(d); toast.success('Follow-up registrado!'); setModalFollowup(false); }}
          onFechar={() => setModalFollowup(false)} />
      </Modal>
    </div>
  );
}

function FormMeta({ clientes, onSalvar, onFechar }) {
  const [form, setForm] = useState({ cliente_id: '', periodo_tipo: 'mensal', periodo: '', meta_valor: '', realizado_valor: '', meta_quantidade: '', realizado_quantidade: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} className="space-y-4">
      <div><label className="form-label">Cliente *</label>
        <select className="form-input" value={form.cliente_id} onChange={e => set('cliente_id', e.target.value)} required>
          <option value="">Selecione...</option>
          {clientes.map(c => <option key={c.id} value={c.id}>{c.razao_social}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="form-label">Período</label>
          <select className="form-input" value={form.periodo_tipo} onChange={e => set('periodo_tipo', e.target.value)}>
            <option value="mensal">Mensal</option>
            <option value="trimestral">Trimestral</option>
            <option value="anual">Anual</option>
          </select>
        </div>
        <div><label className="form-label">Referência (ex: 2025-06)</label><input className="form-input" placeholder="2025-06" value={form.periodo} onChange={e => set('periodo', e.target.value)} /></div>
        <div><label className="form-label">Meta faturamento (R$)</label><input type="number" className="form-input" value={form.meta_valor} onChange={e => set('meta_valor', e.target.value)} /></div>
        <div><label className="form-label">Meta quantidade</label><input type="number" className="form-input" value={form.meta_quantidade} onChange={e => set('meta_quantidade', e.target.value)} /></div>
      </div>
      <div className="flex gap-2"><button type="submit" className="btn-primary flex-1">Criar Meta</button><button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button></div>
    </form>
  );
}

function FormAtualizarMeta({ meta, onSalvar, onFechar }) {
  const [form, setForm] = useState({ realizado_valor: meta.realizado_valor || '', realizado_quantidade: meta.realizado_quantidade || '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const calcStatus = () => {
    const pct = (Number(form.realizado_valor) / Number(meta.meta_valor)) * 100;
    if (pct < 80) return 'nao_atingiu';
    if (pct < 100) return 'parcial';
    if (pct < 110) return 'atingiu';
    return 'superou';
  };
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar({ ...form, status: calcStatus() }); }} className="space-y-4">
      <p className="text-sm text-gray-600">Cliente: <strong>{meta.cliente_id}</strong> · Período: {meta.periodo}</p>
      {meta.meta_valor && <div><label className="form-label">Realizado — faturamento (R$)</label><input type="number" className="form-input" value={form.realizado_valor} onChange={e => set('realizado_valor', e.target.value)} /></div>}
      {meta.meta_quantidade && <div><label className="form-label">Realizado — quantidade</label><input type="number" className="form-input" value={form.realizado_quantidade} onChange={e => set('realizado_quantidade', e.target.value)} /></div>}
      <div className="flex gap-2"><button type="submit" className="btn-primary flex-1">Salvar</button><button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button></div>
    </form>
  );
}

function FormFollowup({ clientes, usuarios, onSalvar, onFechar }) {
  const [form, setForm] = useState({ cliente_id: '', data_sessao: '', observacoes: '', encaminhamentos: '', proxima_sessao: '', responsavel_id: '', checklist: [] });
  const [novoItem, setNovoItem] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addItem = () => {
    if (!novoItem.trim()) return;
    setForm(p => ({ ...p, checklist: [...p.checklist, { item: novoItem, combinado: true, executado: false }] }));
    setNovoItem('');
  };
  const toggleExecutado = (i) => setForm(p => ({
    ...p, checklist: p.checklist.map((c, idx) => idx === i ? { ...c, executado: !c.executado } : c)
  }));
  const remover = (i) => setForm(p => ({ ...p, checklist: p.checklist.filter((_, idx) => idx !== i) }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="form-label">Cliente *</label>
          <select className="form-input" value={form.cliente_id} onChange={e => set('cliente_id', e.target.value)} required>
            <option value="">Selecione...</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.razao_social}</option>)}
          </select>
        </div>
        <div><label className="form-label">Responsável</label>
          <select className="form-input" value={form.responsavel_id} onChange={e => set('responsavel_id', e.target.value)}>
            <option value="">Selecione...</option>
            {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
        <div><label className="form-label">Data da sessão *</label><input type="datetime-local" className="form-input" value={form.data_sessao} onChange={e => set('data_sessao', e.target.value)} required /></div>
        <div><label className="form-label">Próxima sessão</label><input type="datetime-local" className="form-input" value={form.proxima_sessao} onChange={e => set('proxima_sessao', e.target.value)} /></div>
      </div>

      <div>
        <label className="form-label">Checklist do que foi combinado</label>
        <div className="flex gap-2 mt-1">
          <input className="form-input flex-1" placeholder="Ex: Revisar script de vendas" value={novoItem} onChange={e => setNovoItem(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addItem())} />
          <button type="button" onClick={addItem} className="btn-secondary">+ Add</button>
        </div>
        {form.checklist.length > 0 && (
          <div className="mt-2 space-y-1">
            {form.checklist.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-3 py-2">
                <input type="checkbox" checked={item.executado} onChange={() => toggleExecutado(i)} className="accent-brand-600" />
                <span className={item.executado ? 'line-through text-gray-400' : 'text-gray-700'}>{item.item}</span>
                <button type="button" onClick={() => remover(i)} className="ml-auto text-gray-300 hover:text-red-400"><IcX size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div><label className="form-label">Observações</label><textarea className="form-input" rows={3} value={form.observacoes} onChange={e => set('observacoes', e.target.value)} /></div>
      <div><label className="form-label">Encaminhamentos</label><textarea className="form-input" rows={2} value={form.encaminhamentos} onChange={e => set('encaminhamentos', e.target.value)} /></div>
      <div className="flex gap-2"><button type="submit" className="btn-primary flex-1">Registrar Follow-up</button><button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button></div>
    </form>
  );
}
